<template>
  <div 
    class="order-card"
    :class="[
      { 'cursor-move': isDraggable },
      isCompact ? 'py-1.5 px-2' : 'p-3'
    ]"
  >
    <!-- Compact Card Layout -->
    <div 
      class="bg-white dark:bg-dark-800 rounded-lg shadow-sm hover:shadow-md transition-shadow p-1.5 cursor-pointer"
      @click="handleCardClick"
    >
      <!-- First Row: Order Number and Tags -->
      <div class="flex items-center gap-2">
        <span class="text-[10px] font-medium text-gray-900 dark:text-dark-100">{{ order.order_number }}</span>
        <div class="flex items-center gap-1 ml-auto">
          <span 
            :class="getClassificationClass(getClientClassification(order))"
            class="text-[7px] px-1.5 py-0.25 rounded-full"
          >
            {{ getClientClassification(order) }}
          </span>
        </div>
      </div>

      <!-- Second Row: Due Date and Priority -->
      <div class="flex items-center justify-between mt-0.5">
        <span class="text-red-500 dark:text-red-400 text-[10px]">
          Due: {{ formatDate(order.deadline) }}
        </span>
        <span 
          v-if="order.priority"
          :class="getPriorityClass(order.priority)"
          class="text-[9px] px-1.5 py-0.25 rounded-full"
        >
          {{ order.priority }}
        </span>
      </div>

      <!-- Expanded Content -->
      <div v-if="!isCompact" class="mt-2 pt-2 border-t border-gray-100 dark:border-dark-600">
        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-dark-400">Client:</span>
            <span class="font-medium text-gray-900 dark:text-dark-100">{{ order.company_name || order.client_name }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-dark-400">Assigned to:</span>
            <span class="font-medium text-gray-900 dark:text-dark-100">{{ order.assigned_to || 'Unassigned' }}</span>
          </div>
          <div v-if="order.notes" class="flex justify-between">
            <span class="text-gray-600 dark:text-dark-400">Notes:</span>
            <span class="font-medium truncate max-w-[200px] text-gray-900 dark:text-dark-100">{{ order.notes }}</span>
          </div>
        </div>
        
        <button 
          v-if="canMoveToNextStatus"
          @click.stop="$emit('move', order)"
          class="mt-2 w-full flex items-center justify-center px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
        >
          <i class="fas fa-arrow-right mr-1"></i>
          {{ getNextStatusLabel }}
        </button>
      </div>
    </div>

        <!-- Details Modal removed - using parent preview event instead -->
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { ordersApi } from '@/services/api';
import { useOrderStore } from '@/stores/orderStore';
import { getImageUrl as getImageUrlUtil } from '@/utils/imageUtils';

const props = defineProps({
  order: {
    type: Object,
    required: true
  },
  isDraggable: {
    type: Boolean,
    default: false
  },
  isCompact: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['move', 'preview']);

const orderStore = useOrderStore();

// Handle card click - always show modal
const handleCardClick = () => {
  emit('preview', props.order);
};



const getClientClassification = (order) => {
  // Only use classification from client relation (clients table)
  if (order.client && order.client.classification) {
    return order.client.classification;
  }
  
  // Check for client_classification field from the backend response
  if (order.client_classification) {
    return order.client_classification;
  }
  
  // If client relation is not available, use the classification field that was set in the store
  if (order.classification) {
    return order.classification;
  }
  
  // Default if no classification is available
  return 'regular';
};

const getClassificationClass = (classification) => {
  // Map of exact classification strings to their display classes
  const classes = {
    'vip': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    'regular': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    'new': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    'inactive': 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300',
    'preferred': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
  };
  
  // Use the exact type to look up the class, with fallback to regular
  return classes[classification.toLowerCase()] || classes['regular'];
};

// Add a watcher to log classification data when order changes
watch(() => props.order, (newOrder) => {
  console.log(`[Classification Debug] OrderCard - Order ${newOrder.order_number}:`, {
    'order.client?.classification': newOrder.client?.classification,
    'order.classification': newOrder.classification,
    'final classification used': getClientClassification(newOrder)
  });
  
  // Debug order items
  console.log(`[Items Debug] OrderCard - Order ${newOrder.order_number}:`, {
    'has items array': Array.isArray(newOrder.items),
    'items length': newOrder.items?.length || 0,
    'items': newOrder.items,
    'full order object': newOrder
  });
}, { immediate: true, deep: true });

const canMoveToNextStatus = computed(() => {
  // First check if the order is in a final state
  if (props.order.status === 'ready_for_release' || props.order.status === 'ready_release') {
    return false;
  }
  
  // If we have permission data, use it
  if (orderStore.orderPermissions[props.order.id]) {
    return orderStore.orderPermissions[props.order.id].canChange;
  }
  
  // Otherwise, check permission and cache the result
  checkMovePermission();
  
  // Return true by default to avoid flickering, will be updated after API call
  return true;
});

// Add a method to check move permission
const checkMovePermission = async () => {
  try {
    if (!props.order.id) return;
    
    // Skip if we already have the permission data
    if (orderStore.orderPermissions[props.order.id]) return;
    
    const result = await ordersApi.checkStatusChangePermission(props.order.id);
    
    // Store the permission in the store
    orderStore.setOrderPermission(props.order.id, result.canChange, result.nextStatus);
  } catch (error) {
    console.error('Error checking move permission:', error);
    // In case of error, assume no permission
    orderStore.setOrderPermission(props.order.id, false, null);
  }
};

const getNextStatusLabel = computed(() => {
  const status = props.order.status;
  const subStatus = props.order.sub_status;
  
  if (status === 'pending') {
    return 'Move to Production';
  }
  
  if (status === 'in_production') {
    const subStatuses = ['editing', 'printing', 'operations'];
    const currentIndex = subStatuses.indexOf(subStatus);
    
    if (currentIndex < subStatuses.length - 1) {
      return `Move to ${subStatuses[currentIndex + 1].charAt(0).toUpperCase() + subStatuses[currentIndex + 1].slice(1)}`;
    }
    
    return 'Move to Quality Check';
  }
  
  if (status === 'quality_check') {
    return 'Move to Ready for Release';
  }
  
  return '';
});

const getDueDateClass = (deadline) => {
  const today = new Date();
  const dueDate = new Date(deadline);
  const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'text-red-600 font-bold text-sm'; // Overdue
  if (diffDays === 0) return 'text-orange-600 font-bold text-sm'; // Due today
  if (diffDays <= 2) return 'text-yellow-600 font-medium text-sm'; // Almost due
  return 'text-gray-600 text-sm'; // Far from due
};

const getPriorityClass = (priority) => {
  const classes = {
    'urgent': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    'high': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    'normal': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
  };
  return classes[priority] || classes['normal'];
};

const formatCurrency = (value) => {
  if (!value) return '0.00';
  return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-PH', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getImageUrl = (imageUrl) => {
  return getImageUrlUtil(imageUrl);
};

// Modal functionality removed - using parent preview event instead
</script>

<style scoped>
.order-card {
  @apply bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200;
}

.order-card:hover {
  @apply shadow-md;
}

.cursor-move {
  cursor: move;
}

/* Client Type Badge Styles */
.client-type-badge {
  @apply text-xs px-1.5 py-0.5 rounded-full font-medium;
}

/* Priority Badge Styles */
.priority-badge {
  @apply text-xs px-1.5 py-0.5 rounded-full font-medium;
}

/* Expanded Content Animation */
.expanded-content-enter-active,
.expanded-content-leave-active {
  transition: all 0.3s ease-in-out;
  max-height: 200px;
}

.expanded-content-enter-from,
.expanded-content-leave-to {
  max-height: 0;
  opacity: 0;
}

.expanded-content-enter-to,
.expanded-content-leave-from {
  max-height: 200px;
  opacity: 1;
}
</style> 
